const BROWSER_KEY = 'wcf_measurement_browser';
const editableNumericFields = new Map();

export function prepareEditableExpoMeasurements(expo, templateSuffix) {
  if (templateSuffix !== 'custom-sized-products' || !expo) return 0;
  const seen = new WeakSet();
  let changed = 0;
  const visit = (items) => {
    if (!Array.isArray(items)) return;
    items.forEach((item) => {
      if (!item || typeof item !== 'object' || seen.has(item)) return;
      seen.add(item);
      if (item.type === 'number' && labelKey(item.name)) {
        editableNumericFields.set(item.name, { min: item.min, max: item.max });
        item.type = 'text';
        changed += 1;
      }
      visit(item.options);
      visit(item.children);
    });
  };
  visit(expo.allOptionSets);
  visit(expo.optionSets);
  return changed;
}

function restoreNumericInputs() {
  document.querySelectorAll('.expo-steps-overlay--modal input.expo-element-text-field').forEach((control) => {
    const name = /^expo\[(.*)\]$/u.exec(control.name)?.[1];
    const limits = editableNumericFields.get(name);
    if (!limits || control.type === 'number') return;
    control.type = 'number';
    control.min = String(Math.max(1, Number(limits.min) || 1));
    if (limits.max) control.max = limits.max;
    control.step = 'any';
    control.inputMode = 'decimal';
  });
}

const labelKey = (label) => {
  const clean = String(label || '').toLowerCase().replace(/\([^)]*\)/gu, '').replace(/[^a-z]+/gu, ' ').trim();
  if (clean.startsWith('height')) return 'height';
  if (clean.startsWith('weight')) return 'weight';
  if (clean.startsWith('bust') || clean.startsWith('chest')) return 'bust';
  if (clean.startsWith('waist')) return 'waist';
  if (clean.startsWith('hip')) return 'hip';
  if (clean.startsWith('upper arm circumference')) return 'upperArm';
  if (clean.startsWith('shoulder width')) return 'shoulder';
  if (clean.startsWith('inner leg length') || clean.startsWith('inseam')) return 'inseam';
  if (clean.startsWith('crotch length')) return 'crotch';
  if (clean.startsWith('outseam')) return 'outseam';
  if (clean.startsWith('sizes of set worn in the past')) return 'pastSize';
  if (clean.startsWith('scrub set preferences')) return 'preferences';
  return '';
};

const compactNumber = (value) => String(Math.round(value * 10) / 10);
const validNumber = (value) => /^\d+(?:\.\d+)?$/u.test(value) && Number(value) > 0 && Number.isFinite(Number(value));

function parseParts(text) {
  if (typeof text !== 'string' || text.length > 4000) return [];
  return text.split('|').map((part) => {
    const separator = part.indexOf(':');
    if (separator < 1) return null;
    const label = part.slice(0, separator).trim();
    return { label, key: labelKey(label), value: part.slice(separator + 1).replace(/^:+\s*/u, '').trim() };
  }).filter(Boolean);
}

function imperialHeight(value) {
  const match = /^(\d+)\s*'\s*(\d{1,2})\s*(?:"|''|in)?$/u.exec(value);
  if (!match || Number(match[2]) >= 12 || Number(match[1]) < 1) return '';
  return `${Number(match[1])}'${Number(match[2])}"`;
}

const displayLabels = {
  height: 'Height', weight: 'Weight', bust: 'Bust', waist: 'Waist', hip: 'Hips',
  upperArm: 'Upper arm', shoulder: 'Shoulder width', inseam: 'Inseam',
  crotch: 'Crotch length', outseam: 'Outseam', pastSize: 'Previous set size',
  preferences: 'Fit preference',
};

export function formatSavedMeasurements(text) {
  return parseParts(text).filter(({ value }) => value).map(({ label, key, value }) => {
    let displayValue = value;
    if (key === 'height' && /feet\s*(?:&|and)\s*inches/iu.test(label)) {
      displayValue = imperialHeight(value) || value;
    } else if (key === 'weight' && /pounds|lbs/iu.test(label) && validNumber(value)) {
      displayValue = `${value} lb`;
    } else if (/\binches?\b/iu.test(label) && validNumber(value)) {
      displayValue = `${value} in`;
    }
    return { label: displayLabels[key] || label.replace(/:+$/u, ''), value: displayValue };
  });
}

export function prepareMeasurements(text, sourceUnit = 'auto') {
  const parts = parseParts(text).filter((part) => part.key);
  const measurementParts = parts.filter(({ key }) => !['pastSize', 'preferences'].includes(key));
  if (!measurementParts.length) return { ok: false, reason: 'No recognized measurements were found.' };
  let unit = sourceUnit;
  if (unit === 'auto') {
    const explicitImperial = measurementParts.every(({ label, key }) => key === 'height'
      ? /feet\s*&\s*inches|feet\s*and\s*inches/iu.test(label)
      : key === 'weight' ? /pounds|lbs/iu.test(label) : /inches|\bin\b/iu.test(label));
    unit = explicitImperial ? 'imperial' : 'unknown';
  }
  if (!['cm', 'imperial'].includes(unit)) return { ok: false, reason: 'Saved measurement units are unclear.' };

  const values = {};
  for (const { key, value } of parts) {
    if (key === 'pastSize') {
      if (value.length <= 40) values.pastSize = value;
      continue;
    }
    if (key === 'preferences') {
      const options = value.split(/\s*[,;]\s*/u).filter((choice) => ['Loose-fitting', 'Slim-fit cut'].includes(choice));
      if (options.length) values.preferences = options;
      continue;
    }
    if (key === 'height' && unit === 'imperial') {
      const height = imperialHeight(value);
      if (!height) return { ok: false, reason: 'Saved height is invalid.' };
      values.height = height;
      continue;
    }
    if (!validNumber(value)) return { ok: false, reason: 'A saved measurement is invalid.' };
    const number = Number(value);
    if (unit === 'cm') {
      if (key === 'height') {
        const totalInches = Math.round(number / 2.54);
        values.height = `${Math.floor(totalInches / 12)}'${totalInches % 12}"`;
      } else if (key === 'weight') {
        values.weight = String(Math.round(number * 2.2046226218));
      } else {
        values[key] = compactNumber(number / 2.54);
      }
    } else {
      values[key] = value;
    }
  }
  return { ok: Object.keys(values).length > 0, unit, values };
}

const readBrowser = () => {
  try { return localStorage.getItem(BROWSER_KEY) || ''; } catch { return ''; }
};

const setStatus = (card, message) => {
  const node = card.querySelector('[data-wcf-merge-status]');
  if (!node) return;
  node.textContent = message;
  node.hidden = !message;
};

const wait = (milliseconds) => new Promise((resolve) => setTimeout(resolve, milliseconds));

export function setTrackedValue(control, value) {
  const setter = Object.getOwnPropertyDescriptor(Object.getPrototypeOf(control), 'value')?.set;
  if (setter) setter.call(control, value);
  else control.value = value;
  control.dispatchEvent(new Event('input', { bubbles: true }));
  control.dispatchEvent(new Event('change', { bubbles: true }));
}

async function fillCurrentModal(card, values) {
  const productInfo = card.closest('product-info');
  const trigger = productInfo?.querySelector('.expo-steps-trigger-wrap > .expo-steps-trigger')
    || document.querySelector('.expo-steps-trigger-wrap > .expo-steps-trigger');
  if (trigger && trigger.getAttribute('aria-expanded') !== 'true') trigger.click();
  let steps = null;
  for (let attempt = 0; attempt < 12; attempt += 1) {
    steps = document.querySelector('.expo-steps-overlay--modal .expo-steps');
    if (steps) break;
    await wait(200);
  }
  if (!steps) return 0;
  let filled = 0;
  steps.querySelectorAll('.expo-element-wrapper').forEach((field) => {
    const key = labelKey(field.querySelector('.expo-element-label')?.textContent);
    const value = values[key];
    if (!value) return;
    if (key === 'preferences') {
      const selected = new Set(value);
      field.querySelectorAll('input[type="checkbox"]').forEach((control) => {
        if (control.checked !== selected.has(control.value)) control.click();
      });
      filled += 1;
      return;
    }
    const controls = Array.from(field.querySelectorAll('input, textarea, select'))
      .filter((control) => !['hidden', 'radio', 'checkbox', 'file', 'submit', 'button'].includes(control.type));
    if (!controls.length) return;
    controls.forEach((control) => setTrackedValue(control, value));
    filled += 1;
  });
  return filled;
}

export function syncBrowserRow(card, saved = readBrowser()) {
  const browserRow = card.querySelector('[data-wcf-merge-browser]');
  if (!browserRow) return;
  const summary = browserRow.querySelector('[data-wcf-merge-browser-summary]');
  const button = browserRow.querySelector('[data-wcf-merge-apply]');
  if (!saved) {
    delete browserRow.dataset.measurements;
    if (summary) summary.replaceChildren();
    if (button) button.disabled = false;
    browserRow.hidden = true;
    setStatus(card, '');
    return;
  }
  browserRow.dataset.measurements = saved;
  if (summary) {
    const items = formatSavedMeasurements(saved).map(({ label, value }) => {
      const item = summary.ownerDocument.createElement('div');
      item.className = 'wcf-merge-card__measurement';
      const term = summary.ownerDocument.createElement('dt');
      term.textContent = label;
      const description = summary.ownerDocument.createElement('dd');
      description.textContent = value;
      item.append(term, description);
      return item;
    });
    summary.replaceChildren(...items);
  }
  browserRow.hidden = false;
  const result = prepareMeasurements(saved, 'auto');
  if (button) button.disabled = !result.ok;
  setStatus(card, result.ok
    ? ''
    : 'This older browser profile has no clear units. Please enter and check your measurements manually.');
}

export function initializeCard(card) {
  syncBrowserRow(card);

  card.addEventListener('click', async (event) => {
    const clear = event.target.closest('[data-wcf-merge-clear]');
    if (clear) {
      event.preventDefault();
      try { localStorage.removeItem(BROWSER_KEY); } catch {}
      syncBrowserRow(card);
      return;
    }
    const button = event.target.closest('[data-wcf-merge-apply]');
    if (!button) return;
    event.preventDefault();
    const source = button.closest('[data-measurements]');
    const result = prepareMeasurements(source?.dataset.measurements || '', source?.dataset.unit || 'auto');
    if (!result.ok) {
      setStatus(card, 'The saved size or its units could not be verified. Your current measurements were not changed.');
      return;
    }
    button.disabled = true;
    try {
      const filled = await fillCurrentModal(card, result.values);
      setStatus(card, filled
        ? 'Measurements copied into the fit form. Check the values and complete every required field before adding to cart.'
        : 'The fit form was unavailable. Your current measurements were not changed.');
    } finally {
      button.disabled = false;
    }
  });
}

if (typeof document !== 'undefined') {
  const refresh = () => document.querySelectorAll('[data-wcf-merge-card]').forEach((card) => syncBrowserRow(card));
  const start = () => {
    document.querySelectorAll('[data-wcf-merge-card]').forEach(initializeCard);
    const templateSuffix = document.querySelector('product-info[data-wcf-template]')?.dataset.wcfTemplate;
    const prepare = () => prepareEditableExpoMeasurements(window.EXPO, templateSuffix);
    prepare();
    document.addEventListener('click', (event) => {
      if (event.target.closest('.expo-steps-trigger')) prepare();
    }, true);
    const observer = new MutationObserver(() => {
      if (document.querySelector('.expo-steps-overlay--modal')) restoreNumericInputs();
    });
    observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['type'] });
    window.addEventListener('wcf:browser-measurements-changed', refresh);
    window.addEventListener('storage', (event) => {
      if (event.key === BROWSER_KEY) refresh();
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
}
